import { generateToDocFn } from '@dadohr/shared/domainTypes/document';
import {
  IEnrollmentForm,
  IEnrollmentFormSubmission
} from '@dadohr/shared/domainTypes/enrollmentForms';
import {
  AutomationActionType,
  BatchMode,
  ContentType,
  DEFAULT_LATE_REMINDER_MAX,
  DEFAULT_SHEET_MAPPINGS_V1,
  DEFAULT_SHEET_MAPPINGS_V2,
  DestinatioType,
  ExperienceActor,
  ExperienceActorType,
  ExperienceAnchorType,
  ExperienceEventType,
  ExperienceTemplateMessage,
  IExperience,
  IExperienceEvent,
  IExperienceEventTaskDone,
  IExperienceTemplate,
  isContentTemplateBlock,
  isSubTaskTemplateBlock,
  ISubTaskConfirmTemplate,
  ITaskTemplate,
  PARTICIPANT_ACTOR_KEY,
  QuestionType,
  RequirementType,
  SubTaskQuestionAnswer,
  SubTaskType,
  TaskBlockTemplate,
  TaskStatus
} from '@dadohr/shared/domainTypes/experience';
import { DEMO_ORG_IDS } from '@dadohr/shared/domainTypes/org';
import { ScheduleChannelType } from '@dadohr/shared/domainTypes/schedules';
import { compact, keyBy, mapValues, sortBy, times } from 'lodash';
import shortid from 'shortid';
import { refreshTimestamp } from '../firebase';
import { compareTimestamps, ONE_DAY, ONE_MINUTE } from '../time';

const EVENTS_TO_IGNORE: Set<ExperienceEventType> = new Set([
  ExperienceEventType.SUB_TASK_DONE,
  ExperienceEventType.SUB_TASK_UNDONE,
  ExperienceEventType.SUB_TASK_SKIPPED,
  ExperienceEventType.SUB_TASK_UNSKIPPED,
  ExperienceEventType.AUTOMATION_RAN,
  ExperienceEventType.AUTOMATION_SKIPPED,
  ExperienceEventType.AUTOMATION_UNSKIPPED,
  ExperienceEventType.AUTOMATION_ACTION_SKIPPED,
  ExperienceEventType.AUTOMATION_ACTION_UNSKIPPED
]);

const getLatestEvent = <T extends { events: IExperienceEvent[] }>(
  instance: T,
  typesToIgnore: Set<ExperienceEventType> = EVENTS_TO_IGNORE
) => {
  const { events } = instance;
  for (let i = events.length - 1; i >= 0; i--) {
    const ev = events[i];
    if (!typesToIgnore?.has(ev.type)) {
      return ev;
    }
  }
  return null;
};

const getLatestEventForActor = (
  d: IExperience,
  actor: string,
  typesToIgnore: Set<ExperienceEventType> = EVENTS_TO_IGNORE
): IExperienceEvent | null => {
  if (!d.events.length) {
    return null;
  }
  const tasksById = keyBy(d.tasks, (t) => t.id);

  for (let i = d.events.length - 1; i >= 0; i--) {
    const ev = d.events[i];
    if (!typesToIgnore?.has(ev.type)) {
      if (
        ev.type === ExperienceEventType.TASK_START ||
        ev.type === ExperienceEventType.TASK_REMINDER ||
        ev.type === ExperienceEventType.TASK_LATE_REMINDER ||
        ev.type === ExperienceEventType.TASK_DONE
      ) {
        const task = tasksById[ev.taskId];
        if (!task) {
          continue; // this can happen after a task was deleted, but old events are still lingering around
        }
        if (task.actor === actor) {
          return ev;
        }
      }
    }
  }
  return null;
};

export const normalizeEnrollmentForm = (
  d: IEnrollmentForm
): IEnrollmentForm => {
  d.autoEnroll = d.autoEnroll || { active: false };
  return d;
};

export const normalizeEnrollmentFormSubmission = (
  d: IEnrollmentFormSubmission
): IEnrollmentFormSubmission => {
  return d;
};

type PartialBy<T, K extends keyof T> = Omit<T, K> & Partial<Pick<T, K>>;

export const normalizeExperience = (d: IExperience): IExperience => {
  const runEventsMigration = !DEMO_ORG_IDS.includes(d.orgId);
  if (!d.events) {
    d.events = [];
  }
  d.lastEvent = getLatestEvent(d) || null;
  d.messages = d.messages || [];

  const { userId, accessToken } = d as any;

  const findEvent = (type: ExperienceEventType) =>
    d.events.find((x) => x.type === type);

  const enrollAt = findEvent(ExperienceEventType.EXP_ENROLL);
  const expStart = findEvent(ExperienceEventType.EXP_START);

  if (expStart?.at) {
    d.launchAt = expStart.at;
  } else if (enrollAt?.at) {
    d.launchAt =
      compareTimestamps(enrollAt.at, d.startsAt) < 0 ? d.startsAt : enrollAt.at;
  }

  d.userIds = d.userIds || [userId];
  d.accessTokens = d.accessTokens || [accessToken];
  d.anchorOffsetOverride = d.anchorOffsetOverride || null;
  d.messageOverrides = d.messageOverrides || null;
  d.actors = d.actors || [
    {
      type: ExperienceActorType.USER,
      userId,
      key: PARTICIPANT_ACTOR_KEY,
      accessToken
    }
  ];

  d.actors.forEach((actor: PartialBy<ExperienceActor, 'type'>) => {
    if (!actor.type) {
      actor.type = ExperienceActorType.USER;
    }
  });

  d.latestEvents = {};
  d.actors.forEach((a) => {
    const ev = getLatestEventForActor(d, a.key);
    if (ev && d.latestEvents) {
      d.latestEvents[a.key] = ev;
    }
  });

  if (runEventsMigration) {
    d.latestEvents = {};
    d.actors.forEach((a) => {
      const ev = getLatestEventForActor(d, a.key);
      if (ev && d.latestEvents) {
        d.latestEvents[a.key] = ev;
      }
    });
  }

  const completionEvents = keyBy(
    d.events.filter(
      (f): f is IExperienceEventTaskDone =>
        f.type === ExperienceEventType.TASK_DONE
    ),
    (ev) => ev.taskId || ''
  );

  d.tasks.forEach((t) => {
    t.actor = t.actor || PARTICIPANT_ACTOR_KEY;
    t.subTasks.forEach((s) => {
      if ((s as any).type === 'COMPLETE') {
        s.type = SubTaskType.CONFIRM;
      }
      // not a true migration - we could really compute already known dates
      // by looking at events. we might do that later (but it's computationally a bit expensive)
      s.completedAt = s.completedAt || null;
      s.requirementType = s.requirementType || RequirementType.MANDATORY;
      s.parentLayoutElement = s.parentLayoutElement || null;
    });
    t.audience = t.audience || t.templateId;

    if (t.status === TaskStatus.COMPLETE) {
      if (runEventsMigration) {
        const ev = completionEvents[t.id];
        if (ev) {
          t.completedAt = ev.at || null;
          t.completedLate = !!ev.late;
        }
      } else {
        if (!t.completedAt && completionEvents[t.id]) {
          t.completedAt = completionEvents[t.id].at;
        }
      }
    }
  });

  d.automations = mapValues(d.automations || {}, (a) => {
    a.audience = a.audience || a.templateId;

    return a;
  });

  d.ignoredVariables = d.ignoredVariables || [];
  // This briefly used to be an array - we probably have no data like it and can skip this step
  // d.ignoredMissingActors = !d.ignoredMissingActors
  //   ? {}
  //   : Array.isArray(d.ignoredMissingActors) // used to be an array
  //   ? fromPairs(
  //       ((d.ignoredMissingActors as unknown) as IIgnoredMissingActor[]).map(
  //         (x) => [x.key, x]
  //       )
  //     )
  //   : d.ignoredMissingActors;
  d.ignoredMissingActors = d.ignoredMissingActors || {};
  d.instanceErrors = d.instanceErrors || [];

  d.messages.forEach((m) => {
    // previously stored wrongly at times
    if (m.at) {
      m.at = refreshTimestamp(m.at);
    }
  });
  return d;
};

export const toExperienceDoc = generateToDocFn<IExperience>(
  normalizeExperience
);

export const migrateBlocksOnTask = (
  t: Omit<ITaskTemplate, 'blocks'> & { blocks?: TaskBlockTemplate[] },
  forceRebuild = false
) => {
  t.blocks =
    t.blocks && !forceRebuild
      ? t.blocks
      : compact([
          t.description.t && {
            id: t.id,
            type: ContentType.TEXT,
            parentLayoutElement: null,
            data: {
              t: t.description
            }
          },
          ...t.subTasks
        ]);
  return t as ITaskTemplate;
};

export const migrateBlocks = (
  d: Omit<IExperienceTemplate, 'tasks'> & {
    tasks: (Omit<ITaskTemplate, 'blocks'> & { blocks?: TaskBlockTemplate[] })[];
  },
  forceRebuild = false
): IExperienceTemplate => {
  d.tasks.forEach((t) => migrateBlocksOnTask(t, forceRebuild));
  return d as IExperienceTemplate;
};

export const migrateBlocksOnMessage = (
  d: Omit<ExperienceTemplateMessage, 'blocks'> & {
    blocks?: ExperienceTemplateMessage['blocks'];
  },
  forceRebuild = false
) => {
  d.blocks =
    d.blocks && !forceRebuild
      ? d.blocks
      : d.body.t
      ? [
          {
            id: d.id,
            type: ContentType.TEXT,
            data: { t: d.body },
            parentLayoutElement: null
          }
        ]
      : [];
  return d as ExperienceTemplateMessage;
};

const ORG_SPECIFIC_LATE_REMINDER_MIGRATION_SETTINGS: {
  [orgId: string]: number;
} = {
  'IEM1hyNp-': 3,
  _V72kaV7N: 3,
  WzaRSMR8D: 3,
  hXxjeM2wO: 3,

  i364DA3M7: 7,
  OhFDQ3NKE: 7,
  bY227c4GP: 7,
  CXEsFmz3v: 7,
  DWyLE5K_N: 7
};

const migrateRemindersV1ToV2 = (d: IExperienceTemplate) => {
  d.tasks.forEach((t) => {
    if (t.remindersV2) {
      return;
    }
    t.remindersV2 = [];

    let addOnDeadlineReminder = false;

    t.reminders.forEach((r) => {
      if (r.beforeEnd === null) {
        return;
      }
      t.remindersV2.push({
        id: r.id,
        relativeDistanceToDeadline: -r.beforeEnd,
        v: 2
      });
      addOnDeadlineReminder = true;
    });

    if (t.remindUntilDone) {
      addOnDeadlineReminder = true;
      if (t.remindUntilDone.frequency === 'ONCE') {
        if (
          t.remindUntilDone.offset > 0 &&
          t.remindUntilDone.offset !== ONE_DAY
        ) {
          t.remindersV2.push({
            id: shortid(),
            relativeDistanceToDeadline: t.remindUntilDone.offset,
            v: 2
          });
        }
      }
      if (t.remindUntilDone.frequency === 'INFINITE') {
        const interval = t.remindUntilDone.interval;
        const maxLateReminders =
          ORG_SPECIFIC_LATE_REMINDER_MIGRATION_SETTINGS[d.orgId] ||
          DEFAULT_LATE_REMINDER_MAX;
        times(maxLateReminders, (i) => {
          t.remindersV2.push({
            id: shortid(),
            relativeDistanceToDeadline: (i + 1) * interval,
            v: 2
          });
        });
      }
    }
    if (addOnDeadlineReminder) {
      t.remindersV2.push({
        id: `${t.id}-reminder-on-deadline`,
        relativeDistanceToDeadline: 0,
        v: 2
      });
    }

    t.remindersV2 = sortBy(t.remindersV2, (r) => r.relativeDistanceToDeadline);
  });
  return d;
};

const migrateTimeTimings = (d: IExperienceTemplate) => {
  d.tasks.forEach((t) => {
    if (t.timing) {
      return;
    }

    t.timing = {
      type: 'fixed',
      start: t.start,
      end: t.end,
      time: t.startTime
    };
  });

  compact([...d.messages.other, d.messages.outro]).forEach((m) => {
    if (m.timing) {
      return;
    }
    m.timing = {
      type: 'fixed',
      start: m.sendAt,
      time: m.startTime
    };
  });

  d.automations.forEach((a) => {
    if (a.timing) {
      return;
    }

    if (a.at.type !== 'ABSOLUTE') {
      // never existed
      return;
    }

    a.timing = {
      type: 'fixed',
      start: a.at.start,
      time: a.at.startTime
    };
  });
  return d;
};

const DEFAULT_COMPLETION_TEXT = 'Completed at {{ completionTime }}';

/**
 * Make every timeline item's audiences safe for the editors, for ALL template statuses
 * (incl. the ROUGH_DRAFT / ARCHIVED early return below). The task editor reads
 * `audience.completionCriteria` while rendering a task's segments and maps it on every
 * block edit; `audience.blocks` is read everywhere. Audiences written by other paths (the AI
 * assistant, CSV import) could lack them, which crashed the template page. Defaults only —
 * existing values are left as they are.
 */
export const normalizeItemAudiences = (d: Partial<IExperienceTemplate>): void => {
  const fix = (audiences: unknown, isTask: boolean) => {
    if (!Array.isArray(audiences)) return;
    audiences.forEach((a: any) => {
      if (!a || typeof a !== 'object') return;
      if (!Array.isArray(a.blocks)) a.blocks = [];
      if (isTask && !Array.isArray(a.completionCriteria)) a.completionCriteria = [];
    });
  };
  (d.tasks || []).forEach((t: any) => fix(t?.audiences, true));
  (d.automations || []).forEach((a: any) => fix(a?.audiences, false));
  const msgs: any = d.messages;
  if (msgs) {
    [msgs.intro, ...(Array.isArray(msgs.other) ? msgs.other : []), msgs.outro].forEach((m: any) => fix(m?.audiences, false));
  }
};

export const toExperienceTemplateDoc = generateToDocFn<IExperienceTemplate>(
  (d) => {
    // ROUGH_DRAFT and ARCHIVED templates can be in an incomplete state
    // (copilot may have created tasks/subtasks with missing fields, or an
    // archived template predates a migration). The legacy migrations below
    // assume a fully-populated shape and crash on undefined accesses. Skip
    // entirely — ROUGH_DRAFT gets normalized on promotion to DRAFT, and
    // ARCHIVED templates are read-only by definition so missing fields don't
    // matter until/unless they're unarchived (where the BE forces ai_copilot-
    // touched ones back to ROUGH_DRAFT for re-validation).
    if ((d as any).status === 'ROUGH_DRAFT' || (d as any).status === 'ARCHIVED') {
      d.adminTitle = d.adminTitle || '';
      d.tasks = d.tasks || [];
      d.automations = d.automations || [];
      d.messages = d.messages || { intro: null, other: [], outro: null };
      d.supportingActors = d.supportingActors || {};
      d.resources = d.resources || [];
      d.contacts = d.contacts || [];
      normalizeItemAudiences(d);
      return d;
    }
    d.adminTitle = d.adminTitle || '';
    // Guard like the ROUGH_DRAFT/ARCHIVED branch above: a non-draft template can
    // still arrive with no `tasks` (malformed / partially-migrated doc), and the
    // unguarded forEach crashed the whole admin app from the Firestore listener
    // (Sentry DADO-APP-32). Default to [] so it renders empty instead of throwing.
    d.tasks = d.tasks || [];
    d.tasks.forEach((t) => {
      t.customCompletionCriteria = t.customCompletionCriteria || [];

      if ((t.remindUntilDone as any) === true) {
        // old format
        t.remindUntilDone = {
          frequency: 'INFINITE',
          interval: ONE_DAY,
          offset: 0
        };
      }
      if (t.remindUntilDone && !t.remindUntilDone.offset) {
        t.remindUntilDone.offset = 0;
      }
      t.channels = t.channels || [
        { type: ScheduleChannelType.EMAIL, emailFrom: 'email' }
      ];

      t.actor = t.actor || PARTICIPANT_ACTOR_KEY;

      t.subTasks.forEach((s) => {
        if (s.type === 'SUBMIT') {
          if (typeof s.data.to === 'string') {
            const email = (s.data.to as unknown) as string;
            s.data.to = { type: 'EMAIL', email };
          }
          s.data.fileNameFormat =
            s.data.fileNameFormat ||
            '[{{ experience.title }}] {{ participant.fullName }} - {{ task.title }}: {{ label }}';
        }
        if ((s as any).type === 'COMPLETE') {
          ((s as unknown) as ISubTaskConfirmTemplate).type =
            SubTaskType.CONFIRM;
        }

        s.hooks = s.hooks || {};
        s.hooks.onComplete?.forEach((h) => {
          h.storages.forEach((storage) => {
            if (!Object.keys(storage).includes('destinationType')) {
              storage['destinationType'] = DestinatioType.COMPLETION_HOOK;
              if (storage.destinationType === DestinatioType.COMPLETION_HOOK) {
                storage.completionText = DEFAULT_COMPLETION_TEXT;
              }
            }
          });
        });
        if (s.type === SubTaskType.QUESTION) {
          s.data.storages = s.data.storages || [];
          s.data.storages.forEach((storage) => {
            if (!Object.keys(storage).includes('destinationType')) {
              storage['destinationType'] = DestinatioType.SUBTASK_ANSWER;
            }
          });
        }

        s.requirementType = s.requirementType || RequirementType.MANDATORY;

        s.parentLayoutElement = s.parentLayoutElement || null;
      });

      t.blocks = t.blocks || [];
      t.blocks.forEach((b) => {
        if (isSubTaskTemplateBlock(b)) {
          b.hooks = b.hooks || {};
          b.hooks.onComplete?.forEach((h) => {
            h.storages.forEach((storage) => {
              if (!Object.keys(storage).includes('destinationType')) {
                storage['destinationType'] = DestinatioType.COMPLETION_HOOK;
                if (
                  storage.destinationType === DestinatioType.COMPLETION_HOOK
                ) {
                  storage.completionText = DEFAULT_COMPLETION_TEXT;
                }
              }
            });
          });
          if (b.type === SubTaskType.QUESTION) {
            b.data.storages = b.data.storages || [];
            b.data.storages.forEach((storage) => {
              if (!Object.keys(storage).includes('destinationType')) {
                storage['destinationType'] = DestinatioType.SUBTASK_ANSWER;
              }
            });
          }
          if (b.type === SubTaskType.DOCUSIGN_ENVELOPE_SIGN) {
            b.data.description = b.data.description || { t: '', v: 1 };
          }

          if (b.type === SubTaskType.SUBMIT) {
            // Guard: copilot-created ROUGH_DRAFT subtasks may not have data.to yet
            b.data.retentionTimeInDays =
              b.data.retentionTimeInDays ||
              (b.data.to?.type === 'EMAIL' ? 45 : 0);
          }
          b.requirementType = b.requirementType || RequirementType.MANDATORY;
          b.parentLayoutElement = b.parentLayoutElement || null;
        }

        if (isContentTemplateBlock(b)) {
          b.parentLayoutElement = b.parentLayoutElement || null;
        }
      });

      t.startTime = t.startTime || null;
      t.batchMode = t.batchMode || BatchMode.DEFAULT;

      t.catchup = t.catchup || { disabled: false };
    });
    d.contacts = d.contacts || [];
    d.resources = d.resources || [];
    d.resources.forEach((r) => {
      if (r.type === 'GOOGLE_SHEET' && !r.mappings) {
        r.mappings =
          r.v === 2 ? DEFAULT_SHEET_MAPPINGS_V2 : DEFAULT_SHEET_MAPPINGS_V1;
      }
      if (r.type === 'GOOGLE_SHEET') {
        r.additionalSaveParticipantToResourceMoments =
          r.additionalSaveParticipantToResourceMoments || [];
      }
    });
    d.notificationSender = d.notificationSender || {
      name: '',
      replyTo: ''
    };

    // The first version only had plain immediate anchors
    if (d.anchor.v !== 2) {
      d.anchor = {
        type: ExperienceAnchorType.IMMEDIATE,
        offset: {
          type: 'ABSOLUTE',
          duration: 0,
          hour: 9
        },
        autoEnrollmentRejectionThreshold: null,
        autoEnrollmentRejectionEnabled: true,
        v: 2
      };
    }
    if (!d.messages) {
      const introMessage: ExperienceTemplateMessage = {
        id: d.id,
        actor: PARTICIPANT_ACTOR_KEY,
        subject: '[{{ experience.title }}] A new experience starts today!',
        body: d.description,

        batchMode: BatchMode.DEFAULT,
        taskList: { range: 'ALL', variant: 'expanded' },
        audiences: [
          {
            id: d.id,
            name: '',
            color: '',
            rule: 'EVERYONE',
            blocks: [d.id]
          }
        ],
        blocks: d.description.t
          ? [
              {
                id: d.id,
                type: ContentType.TEXT,
                parentLayoutElement: null,
                data: { t: d.description }
              }
            ]
          : [],
        channels: [{ type: ScheduleChannelType.EMAIL, emailFrom: 'email' }],
        sendAt: 0,
        startTime: null,
        timing: {
          type: 'fixed',
          start: 0,
          time: null
        },
        preferUserTimezone: false,
        catchup: { disabled: false }
      };
      d.messages = {
        intro: null, // to be removed
        other: [introMessage],
        outro: null
      };
    }

    d.messages.other = d.messages.other || []; // field used to be nullable

    if (
      d.messages.intro &&
      !d.messages.other.find((m) => m.id === d.messages.intro?.id)
    ) {
      d.messages.other.unshift(d.messages.intro);
    }
    d.messages.intro = null;

    compact([...d.messages.other, d.messages.outro]).forEach((m) => {
      if (!m.channels) {
        m.channels = [{ type: ScheduleChannelType.EMAIL, emailFrom: 'email' }];
      }
      if (!m.sendAt) {
        m.sendAt = 0;
      }
      m.actor = m.actor || PARTICIPANT_ACTOR_KEY;
      migrateBlocksOnMessage(m);
      if (!m.audiences) {
        m.audiences = [
          {
            id: m.id,
            name: '',
            color: '',
            rule: 'EVERYONE',
            blocks: m.blocks.map((b) => b.id)
          }
        ];
      }
      m.blocks.forEach((b) => {
        if (b.type === 'IMAGE') {
          b.data.type = b.data.type || 'url';
          b.data.width = b.data.width || 100;
        }
      });

      if (m.taskList === undefined) {
        m.taskList = { range: 'ALL', variant: 'expanded' };
      }
      if (m.taskList && m.taskList.variant === undefined) {
        m.taskList.variant = 'expanded';
      }

      m.batchMode = m.batchMode || BatchMode.DEFAULT;

      m.catchup = m.catchup || { disabled: false };
    });

    d.supportingActors = d.supportingActors || {};
    Object.values(d.supportingActors).forEach((s) => {
      s.ignoreWhenMissing =
        s.ignoreWhenMissing === undefined ? false : s.ignoreWhenMissing;
      if (s.type === 'staticUser') {
        const x: any = s;
        if (x.userId) {
          // old shape of static user
          s.defaultUserId = x.userId;
          s.ruleBasedUserIds = [];
          delete x['userId'];
        }
      }
    });
    d.automations = d.automations || [];
    d.automations.forEach((a) => {
      if (!a.audiences) {
        a.audiences = [
          {
            id: a.id,
            name: '',
            color: '',
            rule: 'EVERYONE',
            blocks: a.actions.map((b) => b.id)
          }
        ];
      }
      a.actions = (a.actions || []).map((action) => {
        if (
          action.type === AutomationActionType.GOOGLE_CALENDAR_EVENT_INVITATION
        ) {
          action.isRecurring = action.isRecurring || false; ///explicitely set isRecurring to false
          action.sendNoUpdateEmails = action.sendNoUpdateEmails || false;
        }

        if (
          action.type === AutomationActionType.CREATE_DOCUMENT_FROM_TEMPLATE
        ) {
          action.pplToShareWith = action.pplToShareWith || [];
        }

        action.hooks = action.hooks || {};
        return action;
      });

      a.catchup = a.catchup || { disabled: false };
    });

    migrateBlocks(d);
    d.tasks.forEach((t) => {
      if (!t.audiences) {
        t.audiences = [
          {
            id: t.id,
            name: '',
            color: '',
            rule: 'EVERYONE',
            completionCriteria: t.customCompletionCriteria,
            blocks: t.blocks.map((b) => b.id)
          }
        ];
      }
      t.blocks.forEach((b) => {
        if (b.type === 'IMAGE') {
          b.data.type = b.data.type || 'url';
          b.data.width = b.data.width || 100;
        }
        if (b.type === SubTaskType.QUESTION) {
          // Backfill flat-shape QUESTION blocks (legacy + AI-copilot-generated
          // pre-fix docs have data.title / data.type / data.options at the top
          // level instead of the canonical nested data.question map). Without
          // this, line 742 below crashes on undefined.type.
          if (!b.data.question) {
            const flat = b.data as unknown as {
              title?: string;
              type?: string;
              options?: string[];
            };
            const qt =
              flat.type === 'SINGLE_SELECT' ? 'select'
              : flat.type === 'MULTI_SELECT' ? 'multiSelect'
              : 'text';
            if (qt === 'text') {
              b.data.question = {
                type: 'text',
                label: flat.title || '',
                placeholder: '',
                rows: 3,
                sensitive: false,
              } as typeof b.data.question;
            } else {
              b.data.question = {
                type: qt,
                label: flat.title || '',
                options: (flat.options || []).map((label) => ({ id: shortid(), label })),
              } as typeof b.data.question;
            }
            b.data.storages = b.data.storages || [];
          }
          b.data.storages = b.data.storages || [b.data.storage];
          if (b.data.question.type === 'text') {
            b.data.question.placeholder = b.data.question.placeholder || '';
          }
        }
        if (b.type === SubTaskType.SWAG_UP_CHOICE) {
          b.data.swagUpContactEmails = b.data.swagUpContactEmails || [];
        }

        if (b.type === SubTaskType.CREATE_MEETING) {
          b.data.at.duration = b.data.at.duration ?? ONE_MINUTE * 60;
        }
      });
    });

    d.sso = d.sso || null;

    d.expectedEndThreshold = d.expectedEndThreshold ?? 14 * ONE_DAY;

    if ((d.anchor.autoEnrollmentRejectionThreshold as any) === undefined) {
      if (d.anchor.type === ExperienceAnchorType.IMMEDIATE) {
        d.anchor.autoEnrollmentRejectionThreshold = null;
      }
      if (d.anchor.type === ExperienceAnchorType.FIXED) {
        d.anchor.autoEnrollmentRejectionThreshold = ONE_DAY * 30;
      }
      if (d.anchor.type === ExperienceAnchorType.EMPLOYEE_DATE) {
        if (d.anchor.offset.type === 'ABSOLUTE') {
          if (d.anchor.offset.duration < 0) {
            d.anchor.autoEnrollmentRejectionThreshold = Math.min(
              0,
              d.anchor.offset.duration + ONE_DAY * 14
            );
          } else {
            d.anchor.autoEnrollmentRejectionThreshold = ONE_DAY * 14;
          }
        } else {
          d.anchor.autoEnrollmentRejectionThreshold = null;
        }
      }
    }

    // Existing templates default to enabled (true) to maintain backwards compatibility
    if (d.anchor.autoEnrollmentRejectionEnabled === undefined) {
      d.anchor.autoEnrollmentRejectionEnabled = true;
    }

    d.enrollNewUsers = d.enrollNewUsers ?? d.enrollFuture;
    d.enrollModifiedUsers = d.enrollModifiedUsers ?? false;
    d.preferUserTimezone = d.preferUserTimezone ?? false;

    d.previewToken = d.previewToken ?? { active: false };

    d.employeeApp = {
      banner: {
        url: d.employeeApp?.banner?.url || ''
      },
      contacts: d.employeeApp?.contacts || [],
      resources: d.employeeApp?.resources || [],
      instancePageNavStylePerActor:
        d.employeeApp?.instancePageNavStylePerActor || {},
      v: d.employeeApp?.v || 1
    };

    migrateRemindersV1ToV2(d);
    migrateTimeTimings(d);
    normalizeItemAudiences(d);

    return d;
  }
);

const MIGRATED_ANSWER_SUB_TASK_IDS = [
  'O3KQW7RnV', // Chime
  'GHoxxLhq8', // CA
  'Qh1bNFVEoe', // Udemy Flying Start Ema
  '7f6Ss0IMSv', // Udemy Flying Start Amer
  '0RkUxIgVF' // Udemy Flying Start Apac
];
export const toSubTaskQuestionAnswerDoc = generateToDocFn<SubTaskQuestionAnswer>(
  (d) => {
    // See https://app.shortcut.com/emplexp/story/4812
    if (MIGRATED_ANSWER_SUB_TASK_IDS.includes(d.sTplId)) {
      if (d.d.type === QuestionType.TEXT) {
        (d as any).d.type = QuestionType.SUPPORTING_ACTOR;
      }
    }
    return d;
  }
);

export const toEnrollmentFormDoc = generateToDocFn<IEnrollmentForm>(
  normalizeEnrollmentForm
);

export const toEnrollmentFormSubmissionDoc = generateToDocFn<IEnrollmentFormSubmission>(
  normalizeEnrollmentFormSubmission
);
